import React from "react"
import * as yup from "yup"
import { Block } from "baseui/block"
import { H2, H3, Label2, Label4 } from "baseui/typography"
import { FormControl } from "baseui/form-control"
import { Input } from "baseui/input"
import { Select } from "baseui/select"
import { Button, SHAPE } from "baseui/button"
import { get, isArray } from "lodash"
import { CheckIndeterminate } from "baseui/icon"
import { useIntl } from "gatsby-plugin-intl"
import { Modal, ModalBody, ModalButton, SIZE } from "baseui/modal"
import { sendCustomerContact } from "../service/package"

const times = [
  { label: "12:00 am", id: "12:00AM" },
  { label: "12:30 am", id: "12:30AM" },
  { label: "1:00 am", id: "1:00AM" },
  { label: "1:30 am", id: "1:30AM" },
  { label: "2:00 am", id: "2:00AM" },
  { label: "2:30 am", id: "2:30AM" },
  { label: "3:00 am", id: "3:00AM" },
  { label: "3:30 am", id: "3:30AM" },
  { label: "4:00 am", id: "4:00AM" },
  { label: "4:30 am", id: "4:30AM" },
  { label: "5:00 am", id: "5:00AM" },
  { label: "5:30 am", id: "5:30AM" },
  { label: "6:00 am", id: "6:00AM" },
  { label: "6:30 am", id: "6:30AM" },
  { label: "7:00 am", id: "7:00AM" },
  { label: "7:30 am", id: "7:30AM" },
  { label: "8:00 am", id: "8:00AM" },
  { label: "8:30 am", id: "8:30AM" },
  { label: "9:00 am", id: "9:00AM" },
  { label: "9:30 am", id: "9:30AM" },
  { label: "10:00 am", id: "10:00AM" },
  { label: "10:30 am", id: "10:30AM" },
  { label: "11:00 am", id: "11:00AM" },
  { label: "11:30 am", id: "11:30AM" },
  { label: "12:00 pm", id: "12:00PM" },
  { label: "12:30 pm", id: "12:30PM" },
  { label: "1:00 pm", id: "1:00PM" },
  { label: "1:30 pm", id: "1:30PM" },
  { label: "2:00 pm", id: "2:00PM" },
  { label: "2:30 pm", id: "2:30PM" },
  { label: "3:00 pm", id: "3:00PM" },
  { label: "3:30 pm", id: "3:30PM" },
  { label: "4:00 pm", id: "4:00PM" },
  { label: "4:30 pm", id: "4:30PM" },
  { label: "5:00 pm", id: "5:00PM" },
  { label: "5:30 pm", id: "5:30PM" },
  { label: "6:00 pm", id: "6:00PM" },
  { label: "6:30 pm", id: "6:30PM" },
  { label: "7:00 pm", id: "7:00PM" },
  { label: "7:30 pm", id: "7:30PM" },
  { label: "8:00 pm", id: "8:00PM" },
  { label: "8:30 pm", id: "8:30PM" },
  { label: "9:00 pm", id: "9:00PM" },
  { label: "9:30 pm", id: "9:30PM" },
  { label: "10:00 pm", id: "10:00PM" },
  { label: "10:30 pm", id: "10:30PM" },
  { label: "11:00 pm", id: "11:00PM" },
  { label: "11:30 pm", id: "11:30PM" },
]

const TimePicker = ({ start, end, onChange, value }: any) => (
  <Select
    clearable={false}
    maxDropdownHeight={"200px"}
    options={times}
    value={value}
    onChange={({ value }) => onChange(value)}
    overrides={{
      Root: {
        style: () => ({
          paddingRight: start ? "4px" : "0px",
          paddingLeft: end ? "4px" : "0px",
        }),
      },
      IconsContainer: {
        style: () => ({
          paddingRight: "4px",
        }),
      },
      DropdownContainer: {
        style: () => ({
          marginTop: "-6px",
        }),
      },
      Dropdown: {
        style: () => ({
          paddingTop: "0px",
          backgroundColor: "white",
        }),
      },
      DropdownListItem: {
        style: () => ({
          color: "black",
          ":hover": {
            backgroundColor: "#2C2D2F",
            color: "white",
          },
        }),
      },
    }}
  />
)

const PackageContactForm = ({ packageData, nameInputRef }: any) => {
  const [startTime, setStartTime] = React.useState([times[16]])
  const [endTime, setEndTime] = React.useState([times[18]])
  const [name, setName] = React.useState("")
  const [email, setEmail] = React.useState("")
  const [phoneNumber, setPhoneNumber] = React.useState("")
  const [isLoading, setIsLoading] = React.useState(false)
  const [isOpen, setIsOpen] = React.useState(false)
  const [errors, setErrors] = React.useState({})

  const onSuccess = () => {
    setIsLoading(false)
    setIsOpen(true)
    setName("")
    setEmail("")
    setPhoneNumber("")
    setStartTime([times[16]])
    setEndTime([times[18]])
    setErrors({})
  }

  const phoneRegExp = /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/

  const intl = useIntl()

  const validationSchema = yup.object().shape({
    name: yup.string().required(intl.formatMessage({ id: "PleaseEnterName" })),
    email: yup
      .string()
      .email(intl.formatMessage({ id: "InvalidEmail" }))
      .required(intl.formatMessage({ id: "PleaseEnterEmail" })),
    phoneNumber: yup
      .string()
      .matches(phoneRegExp, intl.formatMessage({ id: "InvalidPhone" }))
      .required(intl.formatMessage({ id: "PleaseEnterPhoneNumber" })),
  })

  return (
    <Block
      as={"form"}
      paddingLeft={"16px"}
      paddingRight={"16px"}
      paddingTop={"8px"}
      paddingBottom={"16px"}
    >
      <H3
        color={"primary"}
        $style={{
          textAlign: "center",
        }}
      >
        {intl.formatMessage({ id: "MoreInformation" })}
      </H3>
      <Block display={"flex"} flexDirection={["column", "row", "row", "row"]}>
        <Block flex={1} paddingRight={["0px", "5px", "5px", "5px"]}>
          <FormControl
            label={intl.formatMessage({ id: "Name" })}
            overrides={{
              Label: {
                style: () => ({
                  fontSize: "1em",
                }),
              },
            }}
            error={get(errors, "name", "")}
          >
            <Input
              error={get(errors, "name", false)}
              inputRef={nameInputRef}
              value={name}
              onChange={e => setName(e.currentTarget.value)}
            />
          </FormControl>
        </Block>
        <Block flex={1} paddingLeft={["0px", "5px", "5px", "5px"]}>
          <FormControl
            label={intl.formatMessage({ id: "PhoneNumber" })}
            overrides={{
              Label: {
                style: () => ({
                  fontSize: "1em",
                }),
              },
            }}
            error={get(errors, "phoneNumber", "")}
          >
            <Input
              error={get(errors, "phoneNumber", false)}
              value={phoneNumber}
              onChange={e => setPhoneNumber(e.currentTarget.value)}
            />
          </FormControl>
        </Block>
      </Block>
      <Block display={"flex"} flexDirection={["column", "row", "row", "row"]}>
        <Block flex={1} paddingRight={["0px", "5px", "5px", "5px"]}>
          <FormControl
            label={intl.formatMessage({ id: "Email" })}
            overrides={{
              Label: {
                style: () => ({
                  fontSize: "1em",
                }),
              },
            }}
            error={get(errors, "email", "")}
          >
            <Input
              error={get(errors, "email", false)}
              value={email}
              onChange={e => setEmail(e.currentTarget.value)}
            />
          </FormControl>
        </Block>
        <Block flex={1} paddingLeft={["0px", "5px", "5px", "5px"]}>
          <Label4
            $style={{ fontSize: "1em" }}
            marginTop={"8px"}
            marginBottom={"8px"}
          >
            {intl.formatMessage({ id: "AvailableTimeToCall" })}
          </Label4>
          <Block
            display={"flex"}
            justifyContent={"space-between"}
            alignItems={"end"}
          >
            <FormControl error={get(errors, "startTime", "")}>
              <TimePicker
                value={startTime}
                start
                onChange={(value: any) => setStartTime(value)}
              />
            </FormControl>
            <Block marginTop="12px">
              <CheckIndeterminate />
            </Block>
            <FormControl error={get(errors, "endTime", "")}>
              <TimePicker
                value={endTime}
                end
                onChange={(value: any) => setEndTime(value)}
              />
            </FormControl>
          </Block>
        </Block>
      </Block>
      <Block
        width={"100%"}
        display={"flex"}
        justifyContent={"center"}
        marginTop={"16px"}
      >
        <Block width={["80%", "30%", "30%", "30%"]}>
          <Button
            type={"button"}
            shape={SHAPE.pill}
            $style={{ width: "100%" }}
            isLoading={isLoading}
            onClick={async () => {
              setErrors({})
              try {
                await validationSchema.validate(
                  {
                    email,
                    name,
                    phoneNumber,
                  },
                  { abortEarly: false }
                )
                setIsLoading(true)
                await sendCustomerContact(
                  packageData,
                  email,
                  name,
                  startTime[0].label,
                  endTime[0].label,
                  phoneNumber
                )
                onSuccess()
              } catch (error) {
                setIsLoading(false)
                const allErrors = get(error, "inner")
                if (isArray(allErrors)) {
                  let currentError = {}
                  allErrors.forEach(error => {
                    currentError = {
                      ...currentError,
                      [error.path]: error.message,
                    }
                  })
                  setErrors(currentError)
                }
              }
            }}
          >
            {intl.formatMessage({ id: "Submit" })}
          </Button>
        </Block>
      </Block>
      <Modal
        onClose={() => setIsOpen(false)}
        isOpen={isOpen}
        size={SIZE.default}
        overrides={{
          Root: {
            style: () => ({
              zIndex: 100,
            }),
          },
          Dialog: {
            style: () => ({
              width: "35%",
            }),
          },
        }}
      >
        <ModalBody>
          <Block
            display={"flex"}
            flexDirection={"column"}
            justifyContent={"center"}
            alignItems={"center"}
            $style={{ textAlign: "center" }}
          >
            <Block
              as={"img"}
              src={require("../images/checked.png")}
              width={"8em"}
            />
            <H2>{intl.formatMessage({ id: "Success" })}</H2>
            <Label2>{intl.formatMessage({ id: "ThankYouForContact" })}</Label2>
          </Block>
          <Block
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            marginTop={"35px"}
            marginBottom={"32px"}
          >
            <ModalButton
              onClick={() => {
                setIsOpen(false)
              }}
            >
              {intl.formatMessage({ id: "OK" })}
            </ModalButton>
          </Block>
        </ModalBody>
      </Modal>
    </Block>
  )
}

export default PackageContactForm
